import React, { useEffect, useState } from "react";
import BaseCard from "../../../../../components/ui/BaseCard";
import BaseConfirmModal from "../../../../../components/ui/BaseConfirmModal";
import AddEmpolymentHistoryModal from "../../../components/AddEmpolymentHistoryModal";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  deleteEmploymentHistory,
  fetchEmploymentHistoryList,
} from "../../../../../services/api/employment";
import { useDispatch } from "react-redux";
import { showToast } from "../../../../../store/toast/toastSlice";
import { useCountries } from "../../../../../hooks/useCountries";
import dayjs from "dayjs";

const AddEmployment = ({ userId }) => {
  const dispatch = useDispatch();
  const { getCountryLabel } = useCountries();
  const [selectedEmployment, setSelectedEmployment] = useState(null);
  const isEdit = selectedEmployment ? true : false;
  const [isAddEmploymentModalOpen, setIsAddEmploymentModalOpen] = useState(false);
  const [employmentToDelete, setEmploymentToDelete] = useState(null);
  const [employmentHistoryList, setEmploymentHistoryList] = useState([]);

  const { isPending: isDeleting, mutateAsync: deleteEmploymentHistoryMutation } = useMutation({
    mutationFn: (id) => deleteEmploymentHistory(id, userId),
    enabled: false,
  });

  const { data: employmentListData } = useQuery({
    queryKey: ["employmentHistoryList", userId],
    queryFn: () => fetchEmploymentHistoryList(userId),
    enabled: !!userId,
  });

  useEffect(() => {
    if (employmentListData) {
      setEmploymentHistoryList(employmentListData?.data);
    }
  }, [employmentListData]);

  const handleAddClick = () => {
    setSelectedEmployment(null);
    setIsAddEmploymentModalOpen(true);
  };

  const handleEditEmployment = (employment) => {
    setSelectedEmployment(employment);
    setIsAddEmploymentModalOpen(true);
  };

  const handleDeleteEmployment = async () => {
    try {
      await deleteEmploymentHistoryMutation(employmentToDelete.id);
      setEmploymentHistoryList((prev) => prev.filter((item) => item.id !== employmentToDelete.id));
      dispatch(showToast({ message: "Employment deleted successfully", type: "success" }));
      setEmploymentToDelete(null);
    } catch (error) {
      dispatch(showToast({ message: "Failed to delete employment", type: "error" }));
    }
  };

  return (
    <form>
      <BaseCard className="py-3 px-4 mt-3 border rounded-lg shadow-sm" elevation={0}>
        <div className="bg-light rounded-sm py-2 px-4 mb-4 flex items-center justify-between">
          <p className="text-base font-normal">Employment History</p>
          <i
            className="fa-solid fa-circle-plus cursor-pointer text-secondary text-xl"
            onClick={handleAddClick}
          ></i>
        </div>

        {employmentHistoryList?.map((employment) => (
          <div
            className="bg-light rounded-md py-3 px-4 flex justify-between border mt-4"
            key={employment.id}
          >
            <div>
              <div className="flex mb-1.5">
                <p className="font-medium mr-2">Company Name: </p>
                <p>{employment?.companyName}</p>
              </div>
              <div className="flex mb-1.5">
                <p className="font-medium mr-2">Designation: </p>
                <p>{employment?.designation}</p>
              </div>
              <div className="flex mb-1.5">
                <p className="font-medium mr-2">Country: </p>
                <p>{getCountryLabel(employment?.country)}</p>
              </div>
              <div className="flex">
                <div className="flex">
                  <p className="font-medium">Start Date: </p>
                  <p className="ml-2">
                    {dayjs(employment?.startDate).format("DD/MM/YYYY")}
                    {!employment?.endDate && " (Present)"}
                  </p>
                </div>
                {employment.endDate && (
                  <div className="flex mb-1.5 ml-9">
                    <p className="font-medium">End Date: </p>
                    <p className="ml-2">{dayjs(employment?.endDate).format("DD/MM/YYYY")}</p>
                  </div>
                )}
              </div>
              {employment.description && (
                <div className="mb-1.5">
                  <p className="font-medium mr-2">Experience: </p>
                  <div
                    className="ql-editor !min-h-0 !p-0 text-justify"
                    dangerouslySetInnerHTML={{ __html: employment.description }}
                  />
                </div>
              )}
            </div>
            <div className="flex items-start shrink-0">
              <i
                className="fa-regular fa-trash-can text-red-600 cursor-pointer mr-3"
                title="Delete"
                onClick={() => setEmploymentToDelete(employment)}
              ></i>
              <i
                className="fa-light fa-pencil text-gray-900 cursor-pointer"
                title="Edit"
                onClick={() => handleEditEmployment(employment)}
              ></i>
            </div>
          </div>
        ))}
      </BaseCard>

      {isAddEmploymentModalOpen && (
        <AddEmpolymentHistoryModal
          open={isAddEmploymentModalOpen}
          close={setIsAddEmploymentModalOpen}
          setEmploymentHistoryList={setEmploymentHistoryList}
          selectedEmployment={selectedEmployment}
          setSelectedEmployment={setSelectedEmployment}
          isEdit={isEdit}
          targetUserId={userId}
        />
      )}

      {employmentToDelete && (
        <BaseConfirmModal
          open={!!employmentToDelete}
          onClose={() => setEmploymentToDelete(null)}
          cancelText="Cancel"
          confirmText="Delete"
          confirmButtonColor="error"
          type="warning"
          title="Delete Employment"
          description="Are you sure you want to delete this employment record? This action cannot be undone."
          isLoading={isDeleting}
          onConfirm={handleDeleteEmployment}
        />
      )}
    </form>
  );
};

export default AddEmployment;
